import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuthStore, huidigRefreshToken } from "@/lib/auth-store";
import { isVertrouwdApparaat } from "@/lib/apparaatInstellingen";
import { authApi } from "@/lib/api";

const IDLE_MINUTEN = 30;
const WAARSCHUWING_SECONDEN = 60;
const CHECK_INTERVAL_MS = 30 * 1000;

/** Gedeeld tussen tabbladen: tijdstip van de laatste gebruikersactie. */
export const ACTIVITEIT_KEY = "ks.laatsteActiviteit";
const ACTIVITEIT_SCHRIJF_INTERVAL_MS = 5 * 1000;

function leesGedeeldeActiviteit(): number {
  try {
    const raw = window.localStorage.getItem(ACTIVITEIT_KEY);
    const ts = raw ? Number(raw) : 0;
    return Number.isFinite(ts) ? ts : 0;
  } catch {
    return 0;
  }
}

function schrijfGedeeldeActiviteit(ts: number) {
  try {
    window.localStorage.setItem(ACTIVITEIT_KEY, String(ts));
  } catch {
    /* quota of private mode */
  }
}

/**
 * Meet de inactiviteit van de gebruiker en toont een aftelling
 * 60 seconden voor ze worden uitgelogd.
 *
 * Multi-tab (27-07): sinds de tokens gedeeld zijn over tabbladen zou
 * een achtergrond-tabblad (per definitie inactief) na 30 minuten het
 * gedeelde refresh-token intrekken en daarmee álle tabbladen
 * uitloggen. Daarom telt activiteit nu browser-breed: elk tabblad
 * schrijft (gethrottled) zijn laatste activiteit naar localStorage en
 * de idle-check kijkt naar het maximum van lokaal en gedeeld. Pas als
 * de gebruiker in géén enkel tabblad actief was, volgt de logout.
 *
 * Vertrouwd apparaat (08-09): staat die schakelaar op Profiel aan, dan
 * meet deze guard niets en logt hij nooit uit — bedoeld voor JP's eigen
 * Mac. De grens blijft dan de refresh-token-levensduur van 30 dagen, en
 * 2FA bij het inloggen verandert er niet door. De vlag zit in
 * localStorage en geldt dus alleen op dit toestel.
 *
 * Toetsaccount (sessie 77): een gebruiker met rol "toets" heeft geen
 * idle-uitlog, net als een vertrouwd apparaat (JP-besluit). Alle andere
 * rollen houden de 30 minuten stilte. De rol komt uit de auth-store.
 */
export function IdleGuard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logoutLokaal = useAuthStore((s) => s.logoutLokaal);
  const accessToken = useAuthStore((s) => s.accessToken);
  const isToets = useAuthStore((s) => s.gebruiker?.rol === "toets");

  // Bij het monteren gelezen; een wijziging op Profiel gooit een
  // storage-event zodat een ander tabblad meteen meebeweegt.
  const [vertrouwd, setVertrouwd] = useState(isVertrouwdApparaat);
  useEffect(() => {
    function herlees() {
      setVertrouwd(isVertrouwdApparaat());
    }
    window.addEventListener("storage", herlees);
    return () => window.removeEventListener("storage", herlees);
  }, []);
  const overslaan = vertrouwd || isToets;

  const [waarschuwingOpen, setWaarschuwingOpen] = useState(false);
  const [resterend, setResterend] = useState(WAARSCHUWING_SECONDEN);
  const laatsteActiviteitRef = useRef<number>(Date.now());

  useEffect(() => {
    function markActief() {
      const nu = Date.now();
      laatsteActiviteitRef.current = nu;
      // Gethrottled naar de gedeelde opslag zodat andere tabbladen
      // weten dat de gebruiker nog actief is (zie multi-tab-docstring).
      if (nu - leesGedeeldeActiviteit() >= ACTIVITEIT_SCHRIJF_INTERVAL_MS) {
        schrijfGedeeldeActiviteit(nu);
      }
    }
    const events: Array<keyof DocumentEventMap> = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
    ];
    events.forEach((ev) =>
      document.addEventListener(ev, markActief, { passive: true }),
    );
    return () => {
      events.forEach((ev) => document.removeEventListener(ev, markActief));
    };
  }, []);

  useEffect(() => {
    if (!accessToken || overslaan) return;
    const interval = window.setInterval(() => {
      const laatste = Math.max(
        laatsteActiviteitRef.current,
        leesGedeeldeActiviteit(),
      );
      const inactiefMs = Date.now() - laatste;
      const grensMs = IDLE_MINUTEN * 60 * 1000 - WAARSCHUWING_SECONDEN * 1000;
      if (inactiefMs >= grensMs && !waarschuwingOpen) {
        setResterend(WAARSCHUWING_SECONDEN);
        setWaarschuwingOpen(true);
      } else if (inactiefMs < grensMs && waarschuwingOpen) {
        // Activiteit in een ander tabblad: waarschuwing hier ook weg.
        setWaarschuwingOpen(false);
      }
    }, CHECK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [accessToken, waarschuwingOpen, overslaan]);

  useEffect(() => {
    // Vertrouwd aangezet terwijl de aftelling liep: geen aftelling en
    // geen logout meer; de dialoog gaat hieronder ook dicht.
    if (!waarschuwingOpen || overslaan) return;
    const tick = window.setInterval(() => {
      setResterend((r) => {
        if (r <= 1) {
          window.clearInterval(tick);
          // Laatste check: was de gebruiker intussen actief in een
          // ander tabblad, dan niet uitloggen maar sluiten.
          const grensMs =
            IDLE_MINUTEN * 60 * 1000 - WAARSCHUWING_SECONDEN * 1000;
          const laatste = Math.max(
            laatsteActiviteitRef.current,
            leesGedeeldeActiviteit(),
          );
          if (Date.now() - laatste < grensMs) {
            setWaarschuwingOpen(false);
          } else {
            doeUitloggen();
          }
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => window.clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waarschuwingOpen, overslaan]);

  async function doeUitloggen() {
    setWaarschuwingOpen(false);
    const refresh = huidigRefreshToken();
    try {
      if (refresh) await authApi.logout(refresh);
    } catch {
      /* negeer — lokaal uitloggen is de waarheid */
    }
    logoutLokaal();
    navigate("/login", { replace: true });
  }

  function blijfIngelogd() {
    const nu = Date.now();
    laatsteActiviteitRef.current = nu;
    // Ook delen: dan verdwijnt de waarschuwing in andere tabbladen.
    schrijfGedeeldeActiviteit(nu);
    setWaarschuwingOpen(false);
  }

  return (
    <AlertDialog open={waarschuwingOpen && !overslaan}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("auth.idle_waarschuwing_titel")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("auth.idle_waarschuwing_tekst", { seconden: resterend })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={doeUitloggen}>
            {t("navigatie.uitloggen")}
          </AlertDialogCancel>
          <AlertDialogAction onClick={blijfIngelogd}>
            {t("auth.idle_blijf_ingelogd")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
